import {Component, computed, effect, inject, output, signal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {TranslateModule} from '@ngx-translate/core';
import {HttpClient} from '@angular/common/http';
import {CommentContextService} from '../../services/comment-context.service';
import {SnackBarService} from '../../services/snackbar.service';

export interface CaseDataField {
  path: string;
  label: string;
  value: string;
  empty: boolean;
}

export interface CaseDataGroup {
  path: string;
  displayPath: string;
  depth: number;
  fields: CaseDataField[];
  open: boolean;
}

export interface CustomDataResponse {
  data: Record<string, unknown>;
  // Node paths the tenant configured as visible; absent when the tenant configured no restriction.
  visibleNodes?: string[];
}

@Component({
  selector: 'app-case-data',
  imports: [FormsModule, MatIconButton, MatIcon, TranslateModule],
  templateUrl: './case-data.component.html',
  styleUrl: './case-data.component.scss',
})
export class CaseDataComponent {
  closed = output<void>();

  customData = signal<Record<string, unknown> | null>(null);
  // Node paths configured by the tenant; empty when nothing is configured, which means "show everything".
  visibleNodes = signal<readonly string[]>([]);
  // Set while the user asked to see all nodes despite a configured restriction.
  showAll = signal(false);
  loading = signal(false);
  query = signal('');
  // Paths the user explicitly toggled. Interpreted relative to the current default open state
  // (see groupsDefaultOpen), so a toggle always flips the group away from its default — letting the
  // user collapse an auto-expanded group during a search.
  private readonly toggledGroups = signal<ReadonlySet<string>>(new Set());
  // Last view mode, tracked so we can reset toggles when it changes.
  private lastViewMode = '';
  // Identity of the panel context last loaded, tracked so we can reset the search and toggles when
  // the drawer switches between contexts (e.g. case data -> document data) reusing this component.
  private loadedContext: string | null | undefined = undefined;

  private readonly httpClient = inject(HttpClient);
  private readonly commentContextService = inject(CommentContextService);
  private readonly snackBarService = inject(SnackBarService);

  // Debounced copy of `query` so buildGroups does not re-walk the whole customData tree on every keystroke.
  private readonly debouncedQuery = toSignal(toObservable(this.query).pipe(debounceTime(250)), {initialValue: ''});

  protected readonly getCustomDataLink = computed(() => this.commentContextService.panelHal()?._links.getLink('getCustomData')?.href);

  // The panel is reused for case and document context. A document HAL carries a caseId; a case HAL
  // does not — use that to title the panel "Document data" vs "Case data".
  protected readonly titleKey = computed(() => {
    const panelHal = this.commentContextService.panelHal();
    return panelHal && 'caseId' in panelHal ? 'documentData.title' : 'caseData.title';
  });

  protected readonly hasNodeFilter = computed(() => this.visibleNodes().length > 0);

  protected readonly searchActive = computed(() => this.debouncedQuery().trim().length > 0);

  // The configured nodes only restrict the initial view: the user can opt out with the toggle, and a search always
  // covers all fields.
  protected readonly nodeFilterActive = computed(() => this.hasNodeFilter() && !this.showAll() && !this.searchActive());

  // Groups start expanded whenever the view is already narrowed down — while searching and while the configured
  // nodes restrict it — and collapsed when the whole tree is shown, where expanding everything would overwhelm.
  protected readonly groupsDefaultOpen = computed(() => this.searchActive() || this.nodeFilterActive());

  // Which set of groups is on screen: unrestricted, narrowed to the configured nodes, or search results. Toggles are
  // interpreted against this mode, so they must not survive a change of it.
  private readonly viewMode = computed(() => `${this.searchActive()}|${this.nodeFilterActive()}`);

  // Hidden the moment the user types and back only once the debounce settled, so the button is never clickable while
  // the search suspends the restriction and it would have no visible effect.
  protected readonly showAllToggleVisible = computed(() => this.hasNodeFilter() && !this.searchActive() && this.query().trim().length === 0);

  protected readonly groups = computed(() => {
    const data = this.customData();
    if (!data) {
      return [];
    }
    const query = this.debouncedQuery().trim().toLowerCase();
    return this.buildGroups(data, this.toggledGroups(), query, this.nodeFilterActive() ? this.visibleNodes() : null, this.groupsDefaultOpen());
  });

  protected readonly isEmpty = computed(() => {
    const data = this.customData();
    return !this.loading() && (!data || Object.keys(data).length === 0);
  });

  protected readonly noResults = computed(() => !this.isEmpty() && this.searchActive() && this.groups().length === 0);

  // The configured nodes do not exist in this data set, so the restricted view would be blank without an explanation.
  protected readonly filteredEmpty = computed(() => !this.isEmpty() && this.nodeFilterActive() && this.groups().length === 0);

  constructor() {
    effect(() => {
      const context = this.commentContextService.panelHal()?.identification ?? null;
      if (context !== this.loadedContext) {
        this.loadedContext = context;
        this.query.set('');
        this.toggledGroups.set(new Set());
        this.showAll.set(false);
      }
      const link = this.getCustomDataLink();
      if (link) {
        this.loadCustomData(link);
      } else {
        this.customData.set(null);
        this.visibleNodes.set([]);
      }
    });

    // Reset toggles whenever the view mode changes: a different set of groups is shown and the default open state may
    // flip, so keeping old toggles would leave unrelated groups inverted — a search hit collapsed, say.
    effect(() => {
      const viewMode = this.viewMode();
      if (viewMode !== this.lastViewMode) {
        this.lastViewMode = viewMode;
        this.toggledGroups.set(new Set());
      }
    });
  }

  toggleGroup(path: string) {
    const toggled = new Set(this.toggledGroups());
    if (toggled.has(path)) {
      toggled.delete(path);
    } else {
      toggled.add(path);
    }
    this.toggledGroups.set(toggled);
  }

  clearSearch() {
    this.query.set('');
  }

  toggleShowAll() {
    this.showAll.update(showAll => !showAll);
  }

  downloadCustomData() {
    const data = this.customData();
    if (!data) {
      return;
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'case-customdata.json';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    this.snackBarService.open('caseData.downloadSuccess', 3);
  }

  close() {
    this.closed.emit();
  }

  private loadCustomData(link: string) {
    this.loading.set(true);
    this.httpClient.get<CustomDataResponse>(link).subscribe({
      next: (response) => {
        this.customData.set(response?.data ?? {});
        this.visibleNodes.set(response?.visibleNodes ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.customData.set(null);
        this.visibleNodes.set([]);
        this.loading.set(false);
        this.snackBarService.open('caseData.loadError', 3);
      }
    });
  }

  // Walks the customData object and groups all primitive values by their parent path,
  // one collapsible card per group. While searching, only groups with matching fields are shown.
  // `visibleNodes` restricts the walk to the configured nodes; null means no restriction.
  // An explicit toggle flips `defaultOpen`, so the user can collapse an auto-expanded group (or expand a collapsed one).
  private buildGroups(data: object, toggled: ReadonlySet<string>, query: string, visibleNodes: readonly string[] | null, defaultOpen: boolean): CaseDataGroup[] {
    const order: string[] = [];
    const fieldsByGroup = new Map<string, CaseDataField[]>();
    this.collectFields(data, '', query, visibleNodes, order, fieldsByGroup);
    const groupPaths = new Set(order);
    return order.map(path => ({
      path,
      displayPath: path || '$',
      depth: this.countDisplayedAncestors(path, groupPaths),
      fields: fieldsByGroup.get(path)!,
      open: defaultOpen !== toggled.has(path)
    }));
  }

  private collectFields(node: object, parentPath: string, query: string, visibleNodes: readonly string[] | null, order: string[], fieldsByGroup: Map<string, CaseDataField[]>) {
    for (const [key, value] of this.nodeEntries(node)) {
      const path = parentPath ? `${parentPath}.${key}` : key;
      if (value !== null && typeof value === 'object') {
        this.collectFields(value, path, query, visibleNodes, order, fieldsByGroup);
        continue;
      }
      if (visibleNodes && !this.isVisibleNode(path, visibleNodes)) {
        continue;
      }
      const field = this.toField(path, Array.isArray(node) ? `[${key}]` : key, value);
      if (query && !`${field.label} ${field.value} ${field.path}`.toLowerCase().includes(query)) {
        continue;
      }
      if (!fieldsByGroup.has(parentPath)) {
        fieldsByGroup.set(parentPath, []);
        order.push(parentPath);
      }
      fieldsByGroup.get(parentPath)!.push(field);
    }
  }

  // A configured node always includes its whole subtree, so a field is visible when its path is the node
  // itself or lies below it. Array elements are part of the subtree too, as their index is just another segment.
  private isVisibleNode(path: string, visibleNodes: readonly string[]): boolean {
    return visibleNodes.some(node => path === node || path.startsWith(`${node}.`));
  }

  private nodeEntries(node: object): [string, unknown][] {
    return Array.isArray(node)
      ? node.map((value, index) => [String(index), value] as [string, unknown])
      : Object.entries(node);
  }

  private toField(path: string, label: string, value: unknown): CaseDataField {
    const text = this.stringify(value);
    const empty = text.trim() === '';
    return {path, label, value: empty ? '–' : text, empty};
  }

  // Only ever called with primitives (objects recurse in collectFields); the default
  // branch exists for type safety so no value falls back to '[object Object]'.
  private stringify(value: unknown): string {
    if (value == null) {
      return '';
    }
    switch (typeof value) {
      case 'string':
        return value;
      case 'number':
      case 'bigint':
      case 'boolean':
        return value.toString();
      default:
        return JSON.stringify(value) ?? '';
    }
  }

  // Indentation depth relative to the groups actually shown: intermediate object levels
  // without own primitive fields produce no card, so counting raw path segments would
  // indent a group under a card that is not its ancestor and draw dangling guide lines.
  private countDisplayedAncestors(path: string, groupPaths: ReadonlySet<string>): number {
    let depth = 0;
    const segments = path.split('.');
    for (let i = 1; i < segments.length; i++) {
      if (groupPaths.has(segments.slice(0, i).join('.'))) {
        depth++;
      }
    }
    return depth;
  }
}
